"use client"

import { Pencil, Trash2, type LucideIcon } from "lucide-react"
import * as Icons from "lucide-react"
import type { Student } from "../../../../lib/students-data"
import { Badge } from "@/components/ui/badge"

interface StudentTableRowProps {
  student: Student
  onEdit: (student: Student) => void
  onDelete: (id: string) => void
}

export function StudentTableRow({ student, onEdit, onDelete }: StudentTableRowProps) {
  const CareerIcon = (Icons[student.careerIcon as keyof typeof Icons] as LucideIcon) || Icons.Briefcase

  const getGPAColor = (gpa: string) => {
    const gpaValue = Number.parseFloat(gpa)
    if (gpaValue >= 4.5) return "bg-green-100 text-green-800"
    if (gpaValue >= 4.0) return "bg-blue-100 text-blue-800"
    if (gpaValue >= 3.5) return "bg-yellow-100 text-yellow-800"
    return "bg-orange-100 text-orange-800"
  }

  const getInitialsColorClass = (color: string) => {
    const colorMap: Record<string, string> = {
      orange: "bg-orange-100 text-orange-600 border-orange-200",
      purple: "bg-purple-100 text-purple-600 border-purple-200",
      pink: "bg-pink-100 text-pink-600 border-pink-200",
      indigo: "bg-indigo-100 text-indigo-600 border-indigo-200",
      teal: "bg-teal-100 text-teal-600 border-teal-200",
      blue: "bg-blue-100 text-blue-600 border-blue-200",
      green: "bg-green-100 text-green-600 border-green-200",
      amber: "bg-amber-100 text-amber-600 border-amber-200",
      red: "bg-red-100 text-red-600 border-red-200",
      cyan: "bg-cyan-100 text-cyan-600 border-cyan-200",
    }
    return colorMap[color] || "bg-gray-100 text-gray-600 border-gray-200"
  }

  return (
    <tr className="hover:bg-muted/50 transition-colors">
      <td className="p-5">
        <div className="flex items-center gap-3">
          <div
            className={`h-10 w-10 rounded-full flex items-center justify-center border-2 border-white shadow-sm ${getInitialsColorClass(student.initialsColor)}`}
          >
            <span className="font-bold text-sm">{student.initials}</span>
          </div>
          <div>
            <p className="font-bold text-foreground">{student.name}</p>
            <p className="text-xs text-muted-foreground">ID: {student.studentId}</p>
          </div>
        </div>
      </td>
      <td className="p-5 text-sm text-foreground">{student.class}</td>
      <td className="p-5">
        <Badge variant="outline" className={`${getGPAColor(student.gpa)}`}>
          {student.gpa} / 5.0
        </Badge>
      </td>
      <td className="p-5 text-sm text-foreground">
        <div className="flex items-center gap-2">
          <CareerIcon className="w-4 h-4 text-primary" />
          <span>{student.careerPath}</span>
        </div>
      </td>
      <td className="p-5 text-sm text-foreground">
        <p className="font-medium">{student.guardianName}</p>
        <p className="text-xs text-muted-foreground">{student.guardianPhone}</p>
      </td>
      <td className="p-5 text-right">
        <div className="flex justify-end gap-2">
          <button
            onClick={() => onEdit(student)}
            className="p-2 text-primary hover:bg-teal-50 rounded-lg transition-colors"
            title="Edit"
          >
            <Pencil className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDelete(student.id)}
            className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </td>
    </tr>
  )
}
